import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import {
	IBAN_COMPONENT_ELEMENT_ID,
	TEST_ELEMENT_IDS,
} from '@dealstack/shared/tests/testElementIds';
import { BankAccountTile } from '@dealstack/frontend/shared/components/BankAccountTile/BankAccountTile.tsx';
import { BankAccountDeleteDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountDeleteDrawer.tsx';
import { Box, Button, Flex, Select, Text } from '@mantine/core';
import { VerificationCode } from '@dealstack/frontend/shared/components/VerificationCode/VerificationCode';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
	PatchShareholderInvestorBankAccountDto,
	ShareholderInvestorPersonBankAccountsRes,
} from '@dealstack/shared/lib/generated';
import { BankAccountForm } from '@dealstack/frontend/shared/components/BankAccountForm/BankAccountForm.tsx';
import { useBankAccountWithCodeValidation } from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.ts';
import { BANK_ACCOUNT_STATE } from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.types.ts';
import {
	isBankAccountConfirmationPending,
	shouldShowInvestorStatusBadge,
} from '@dealstack/frontend/shared/components/BankAccountWithCodeValidation/useBankAccountWithCodeValidation.utils.ts';
import { BankAccountFormValues } from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import { toBankAccountPayload } from '@dealstack/frontend/shared/components/BankAccountForm/BankAccountForm.utils.ts';
import type { BankAccountVerifyFn } from '@dealstack/frontend/shared/components/BankAccountForm/useBankAccountForm.types.ts';
import {
	upsertBankAccount,
	validateShareholderBankAccount,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investor.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';

interface Props {
	disableNoAccountAlert?: boolean;
	investmentWorkflowId?: string;
	kycWorkflowId?: string;
	onMutationFinish?: () => void;
	personId?: string;
	dealId?: string;
	bankAccountsData: ShareholderInvestorPersonBankAccountsRes[];
	bankAccountsLoading: boolean;
	refetchBankAccounts: () => void;
	dataTestId?: string;
	inferredCountryCode?: string;
}

export const BankAccountWithCodeValidation = ({
	disableNoAccountAlert,
	investmentWorkflowId,
	kycWorkflowId,
	onMutationFinish,
	dealId,
	personId,
	bankAccountsData,
	bankAccountsLoading,
	refetchBankAccounts,
	dataTestId,
	inferredCountryCode,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();

	const {
		magicCode,
		magicCodeError,
		pageMode,
		setPageMode,
		setMagicCodeError,
		setMagicCode,
		pendingBankAccountId,
		setPendingBankAccountId,
		requestBankAccountCode,
		requestBankAccountCodeLoading,
		confirmBankAccount,
		confirmBankAccountLoading,
		upsertBankAccountRequest,
		upsertBankAccountRequestLoading,
		deleteBankAccountRequest,
		deleteBankAccountLoading,
		deleteBankAccountSuccess,
		resetDeleteBankAccount,
	} = useBankAccountWithCodeValidation();

	const [accountPendingDeletion, setAccountPendingDeletion] = useState<string | null>(null);

	const [selectedPersonId, setSelectedPersonId] = useState<string>();
	// An existing account renders as a tile until its edit button opens the form.
	const [isEditingAccount, setIsEditingAccount] = useState(false);
	// Draft shell created lazily on the first IBAN lookup; the final save updates it.
	const shellBankAccountIdRef = useRef<string | null>(null);

	const currentPersonId = useMemo(() => {
		if (personId) {
			return personId;
		}
		if (dealId) {
			return bankAccountsData?.find((data) => data.dealId === dealId)?.personId;
		}
		return selectedPersonId ?? bankAccountsData?.[0]?.personId;
	}, [bankAccountsData, dealId, personId, selectedPersonId]);

	const bankAccountData = useMemo(
		() =>
			(bankAccountsData ?? []).find((data) => data.personId === currentPersonId)
				?.bankAccounts[0],
		[bankAccountsData, currentPersonId]
	);

	const shouldShowDealSelect = !personId && !dealId;
	const isInCodeMode = pageMode === BANK_ACCOUNT_STATE.CODE;
	// An abandoned empty draft shell counts as "no account" — the form shows directly.
	const accountWithDetails =
		bankAccountData && bankAccountData.identifiers.length > 0 ? bankAccountData : undefined;
	const accountAwaitingConfirmation =
		accountWithDetails && isBankAccountConfirmationPending(accountWithDetails.status)
			? accountWithDetails
			: undefined;

	const userPersonDeals = useMemo(
		() =>
			(bankAccountsData ?? []).map((data) => ({
				label: data.dealName,
				value: data.personId,
			})),
		[bankAccountsData]
	);

	useEffect(() => {
		refetchBankAccounts();
	}, [personId]);

	useEffect(() => {
		setIsEditingAccount(false);
	}, [currentPersonId]);

	// Ensures the draft account exists for this person, then runs the scoped lookup so
	// the attempt is logged against it.
	const verifyIdentifiers: BankAccountVerifyFn = async (dto, { countryCode }) => {
		let accountId = bankAccountData?.id ?? shellBankAccountIdRef.current;
		if (!accountId) {
			const created = await upsertBankAccount({
				body: { countryCode: countryCode ?? undefined },
				personId: currentPersonId ?? '',
			});
			accountId = created.payload?.id ?? null;
			if (!accountId) {
				throw new Error('Failed to create the bank account');
			}
			shellBankAccountIdRef.current = accountId;
		}
		return await validateShareholderBankAccount({ bankAccountId: accountId, body: dto });
	};

	const requestConfirmationCode = (bankAccountId: string) => {
		requestBankAccountCode(
			{ bankAccountId },
			{
				onSuccess: () => {
					setPendingBankAccountId(bankAccountId);
					setPageMode(BANK_ACCOUNT_STATE.CODE);
				},
				// The save has already succeeded at this point — only the code email
				// failed. Saving again re-requests a code for the still-pending account.
				onError: () => {
					showToastV2({
						title: tCommonUi('profile.banking_details.request_code.error'),
						state: 'error',
					});
				},
			}
		);
	};

	const startConfirmation = () => {
		if (accountAwaitingConfirmation) {
			requestConfirmationCode(accountAwaitingConfirmation.id);
		}
	};

	const submitVerificationCode = async () => {
		setMagicCodeError('');
		if (!pendingBankAccountId || !magicCode) {
			showToastV2({
				title: tCommonUi('profile.banking_details.save_new.error'),
				state: 'error',
			});
			return;
		}

		await confirmBankAccount(
			{ bankAccountId: pendingBankAccountId, magicCode },
			{
				onError: () => {
					setMagicCodeError(tCommonUi('profile.banking_details.enter_code.error'));
				},
				onSuccess: (response) => {
					if (response.payload?.confirmed) {
						refetchBankAccounts();

						showToastV2({
							title: tCommonUi('profile.banking_details.save_new.success'),
							dataTestId: IBAN_COMPONENT_ELEMENT_ID.UPDATE_BANK_ACCOUNT_SUCCESS_TOAST,
						});
						setMagicCode('');
						setPendingBankAccountId(null);
						setPageMode(BANK_ACCOUNT_STATE.FORM);
						setIsEditingAccount(false);
						onMutationFinish?.();
					} else {
						setMagicCode('');
						showToastV2({
							title: tCommonUi('profile.banking_details.verification_code.invalid'),
							state: 'error',
							dataTestId: IBAN_COMPONENT_ELEMENT_ID.INVALID_CODE_TOAST,
						});
					}
				},
			}
		);
	};

	// The account saves first (resting unconfirmed), then the emailed code confirms it.
	// Without the code round the save itself submits the account for admin review.
	const submitBankAccountDetails = async (body: PatchShareholderInvestorBankAccountDto) => {
		upsertBankAccountRequest(
			{
				body: { ...body, investmentWorkflowId, kycWorkflowId },
				personId: currentPersonId ?? '',
			},
			{
				onSuccess: (response) => {
					refetchBankAccounts();
					const savedId =
						response.payload?.id ?? body.id ?? shellBankAccountIdRef.current;
					shellBankAccountIdRef.current = null;
					if (!savedId) {
						showToastV2({
							title: tCommonUi('profile.banking_details.save_new.error'),
							state: 'error',
						});
						return;
					}

					if (!isBankAccountConfirmationPending(response.payload?.status)) {
						showToastV2({
							title: tCommonUi('profile.banking_details.save_new.success'),
							dataTestId: IBAN_COMPONENT_ELEMENT_ID.UPDATE_BANK_ACCOUNT_SUCCESS_TOAST,
						});
						setPageMode(BANK_ACCOUNT_STATE.FORM);
						setIsEditingAccount(false);
						onMutationFinish?.();
						return;
					}
					requestConfirmationCode(savedId);
				},
				onError: () => {
					setMagicCode('');
					setPageMode(BANK_ACCOUNT_STATE.FORM);
					showToastV2({
						title: tCommonUi('profile.banking_details.save_new.error'),
						state: 'error',
					});
				},
			}
		);
	};

	const handleBankAccountFormSubmit = async (
		formValues: BankAccountFormValues,
		bankAccountId?: string
	) => {
		const payload = toBankAccountPayload(formValues);

		if (!payload) {
			return;
		}

		void submitBankAccountDetails({
			...payload,
			id: bankAccountId ?? shellBankAccountIdRef.current ?? undefined,
		});
	};

	if (bankAccountsLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	const renderAccountContent = () => {
		if (isInCodeMode) {
			return (
				<>
					<VerificationCode
						numberOfVerificationCodeInputs={6}
						value={magicCode}
						onChange={setMagicCode}
						label={
							<Box mb="1rem">
								<Text>{tCommonUi('profile.banking_details.enter_code.title')}</Text>
							</Box>
						}
						errorMessage={magicCodeError}
						size="LARGE"
					/>
					<Button
						variant="primary"
						w="100%"
						mt="1.5rem"
						disabled={magicCode.length !== 6 || confirmBankAccountLoading}
						onClick={submitVerificationCode}
						data-testid={IBAN_COMPONENT_ELEMENT_ID.VERIFY_CODE_BUTTON}
					>
						{tCommonUi('profile.banking_details.enter_code.submit_button')}
					</Button>
				</>
			);
		}
		if (accountWithDetails && !isEditingAccount) {
			return (
				<>
					{accountAwaitingConfirmation && (
						<InfoBox
							iconVariant="information"
							title={tCommonUi('profile.banking_details.pending_confirmation.title')}
							description={tCommonUi(
								'profile.banking_details.pending_confirmation.text'
							)}
							dataTestId={IBAN_COMPONENT_ELEMENT_ID.PENDING_CONFIRMATION_ALERT}
						>
							<Button
								variant="outline"
								w="fit-content"
								fw={400}
								disabled={requestBankAccountCodeLoading}
								onClick={startConfirmation}
								data-testid={IBAN_COMPONENT_ELEMENT_ID.CONFIRM_ACCOUNT_BUTTON}
							>
								{tCommonUi('profile.banking_details.pending_confirmation.button')}
							</Button>
						</InfoBox>
					)}
					<BankAccountTile
						bankAccount={accountWithDetails}
						hasEditPermission
						withBorder
						showStatusBadge={shouldShowInvestorStatusBadge(accountWithDetails.status)}
						statusBadgeTestId={TEST_ELEMENT_IDS.CAP_BANK_ACCOUNT_TILE}
						tileTestId={TEST_ELEMENT_IDS.INVESTOR_FLOW_BANK_ACCOUNT_TILE}
						editBtnTestId={TEST_ELEMENT_IDS.EDITABLE_TILE_EDIT_BUTTON}
						deleteBtnTestId={TEST_ELEMENT_IDS.EDITABLE_TILE_DELETE_BUTTON}
						onEdit={() => setIsEditingAccount(true)}
						onRemove={() => setAccountPendingDeletion(accountWithDetails.id)}
					/>
				</>
			);
		}
		return (
			<BankAccountForm
				hasEditPermission={true}
				onSubmit={handleBankAccountFormSubmit}
				isRequestLoading={upsertBankAccountRequestLoading || requestBankAccountCodeLoading}
				bankAccount={bankAccountData ?? null}
				inferredCountryCode={inferredCountryCode}
				verifyIdentifiers={verifyIdentifiers}
			/>
		);
	};

	return (
		<Flex direction="column" gap="1rem" data-testid={dataTestId}>
			{shouldShowDealSelect ? (
				<Select
					label={tCommonUi('profile.banking_details.deal_name.label')}
					data={userPersonDeals}
					value={currentPersonId}
					disabled={isInCodeMode}
					onChange={(value) => setSelectedPersonId(value ?? undefined)}
					mb="1.25rem"
					size="md"
				/>
			) : null}

			{!disableNoAccountAlert && !bankAccountData && (
				<InfoBox
					iconVariant="information"
					title={tCommonUi('profile.banking_details.no_account_alert.title')}
					description={tCommonUi('profile.banking_details.no_account_alert.text')}
					dataTestId={IBAN_COMPONENT_ELEMENT_ID.NO_ACCOUNT_ALERT_WRAPPER}
				/>
			)}

			{renderAccountContent()}

			<BankAccountDeleteDrawer
				opened={!!accountPendingDeletion}
				onClose={() => {
					setAccountPendingDeletion(null);
					resetDeleteBankAccount();
				}}
				bankAccount={accountWithDetails ?? null}
				deleteLoading={deleteBankAccountLoading}
				deleteSuccess={deleteBankAccountSuccess}
				handleDelete={() => {
					if (!accountPendingDeletion) {
						return;
					}

					deleteBankAccountRequest(
						{ bankAccountId: accountPendingDeletion },
						{
							onSuccess: () => {
								// Back to the entry form: the flow shows one account, and the
								// investor deleted it to enter a different one.
								setAccountPendingDeletion(null);
								shellBankAccountIdRef.current = null;
								setIsEditingAccount(false);
								refetchBankAccounts();
								onMutationFinish?.();
							},
							onError: () => {
								showToastV2({
									title: tCommonUi('profile.banking_details.delete.error', {
										defaultValue: 'The bank account could not be deleted',
									}),
									state: 'error',
								});
							},
						}
					);
				}}
			/>
		</Flex>
	);
};
